import { Sort } from '@/types/sort.type';
import { EndpointOptions } from '@/types/endpoint.options';
import { ReviewAnswerDto } from '@/contracts/marketplace/reviews';
import { AibackReportPdfPayloadEntity, type AibackReportSummaryEntity } from '@/entities/aiback-report.entity';
import {
    AnswerTemplateUpsertPayload,
    ApproveReviewAnswerDraftPayload,
    AutoReplyScenarioPayload,
    DownloadCsvQuery,
    DownloadCsvResult,
    EnqueueAiSelectedAnalysisPayload,
    EnqueueAiSelectedAnalysisResponse,
    GenerateAiAnswerOptions,
    GenerateAiAnswerPayload,
    GenerateAiAnswerResponse,
    GenerateManualAiAnswerPayload,
    GetReportSummaryOptions,
    GetReviewIdsForSelectionQuery,
    GetReviewsQuery,
    ReviewsReportSummaryQuery,
    SelectedAutoRepliesRunPayload,
} from '@/repositories/marketplace-reviews.repository/marketplace-reviews.types';
import { MarketplaceReviewsTransportClient } from '@/repositories/marketplace-reviews.repository/transport.client';
import { consumeReportSummaryStream } from '@/repositories/marketplace-reviews.repository/report-summary-stream.client';
import {
    createRepositoryApiErrorFromFetchResponse,
    normalizeRepositoryError,
} from '@/repositories/repository-api-error';

class MarketplaceReviewsRepository {
    private readonly transport: MarketplaceReviewsTransportClient;

    constructor() {
        this.transport = new MarketplaceReviewsTransportClient();
    }

    getReview(id: string, options?: EndpointOptions) {
        return this.transport.getReview(id, options);
    }

    getReviews(query: GetReviewsQuery, options?: EndpointOptions) {
        return this.transport.getReviews(query, options);
    }

    getReviewsPageGroups(options?: EndpointOptions) {
        return this.transport.getReviewsPageGroups(options);
    }

    async getReportSummary(query?: ReviewsReportSummaryQuery, options?: GetReportSummaryOptions) {
        if (!options?.onProgress) {
            return await this.transport.getReportSummary(query, options);
        }

        try {
            const response = await this.transport.startReportSummaryStream(query, options);
            if (!response.ok) {
                throw await createRepositoryApiErrorFromFetchResponse({
                    response,
                    fallbackMessage: `Failed to load AIBACK report summary (HTTP ${response.status})`,
                    fallbackCode: 'reporting.transport.report_summary_load_failed',
                    fallbackCategory: 'reporting',
                });
            }

            const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
            if (contentType.includes('application/json')) {
                return {
                    data: (await response.json()) as AibackReportSummaryEntity,
                };
            }

            return {
                data: await consumeReportSummaryStream(response, {
                    onProgress: options.onProgress,
                }),
            };
        } catch (error) {
            throw await normalizeRepositoryError({
                error,
                fallbackMessage: 'Failed to load AIBACK report summary',
                fallbackCode: 'reporting.transport.report_summary_load_failed',
                fallbackCategory: 'reporting',
            });
        }
    }

    async getReviewIdsForSelection(query: GetReviewIdsForSelectionQuery, options?: EndpointOptions) {
        return await this.transport.getReviewIdsForSelection(query, options);
    }

    getReviewSelectionSession(sessionId: string, options?: EndpointOptions) {
        return this.transport.getReviewSelectionSession(sessionId, options);
    }

    getReviewSelectionReconcileStatus(sessionId: string, requestId: string, options?: EndpointOptions) {
        return this.transport.getReviewSelectionReconcileStatus(sessionId, requestId, options);
    }

    selectAllReviewSelectionSession(
        params: { sessionId: string; search?: string[] } & Partial<Sort>,
        options?: EndpointOptions,
    ) {
        return this.transport.selectAllReviewSelectionSession(params, options);
    }

    reconcileReviewSelectionSession(
        params: { sessionId: string; search?: string[]; requestId?: string } & Partial<Sort>,
        options?: EndpointOptions,
    ) {
        return this.transport.reconcileReviewSelectionSession(params, options);
    }

    updateReviewSelectionSession(
        sessionId: string,
        data: {
            addIds?: string[];
            removeIds?: string[];
        },
        options?: EndpointOptions,
    ) {
        return this.transport.updateReviewSelectionSession(sessionId, data, options);
    }

    clearReviewSelectionSession(sessionId: string, options?: EndpointOptions) {
        return this.transport.clearReviewSelectionSession(sessionId, options);
    }

    getSelectedReviewsForAnswer(selectionSessionId: string, options?: EndpointOptions) {
        return this.transport.getSelectedReviewsForAnswer(selectionSessionId, options);
    }

    createReviewAnswers(data: ReviewAnswerDto[], options?: EndpointOptions) {
        return this.transport.createReviewAnswers(data, options);
    }

    updateReviewAnswer(data: ReviewAnswerDto, options?: EndpointOptions) {
        return this.transport.updateReviewAnswer(data, options);
    }

    getHistory(id: string, options?: EndpointOptions) {
        return this.transport.getHistory(id, options);
    }

    generateAiAnswer(data: GenerateAiAnswerPayload, options?: GenerateAiAnswerOptions) {
        return this.transport.generateAiAnswer(data, options);
    }

    generateManualAiAnswer(data: GenerateManualAiAnswerPayload, options?: EndpointOptions) {
        return this.transport.generateManualAiAnswer(data, options);
    }

    enqueueAiSelectedAnalysis(data: EnqueueAiSelectedAnalysisPayload, options?: EndpointOptions) {
        return this.transport.enqueueAiSelectedAnalysis(data, options);
    }

    getAiSelectedAnalysisProgress(operationId: string, options?: EndpointOptions) {
        return this.transport.getAiSelectedAnalysisProgress(operationId, options);
    }

    getAnswerTemplates(options?: EndpointOptions) {
        return this.transport.getAnswerTemplates(options);
    }

    createAnswerTemplate(data: AnswerTemplateUpsertPayload, options?: EndpointOptions) {
        return this.transport.createAnswerTemplate(data, options);
    }

    updateAnswerTemplate(templateId: string, data: AnswerTemplateUpsertPayload, options?: EndpointOptions) {
        return this.transport.updateAnswerTemplate(templateId, data, options);
    }

    deleteAnswerTemplate(templateId: string, options?: EndpointOptions) {
        return this.transport.deleteAnswerTemplate(templateId, options);
    }

    getAutoReplyScenarios(options?: EndpointOptions) {
        return this.transport.getAutoReplyScenarios(options);
    }

    enqueueSelectedAutoRepliesRun(data: SelectedAutoRepliesRunPayload, options?: EndpointOptions) {
        return this.transport.enqueueSelectedAutoRepliesRun(data, options);
    }

    createAutoReplyScenario(data: AutoReplyScenarioPayload, options?: EndpointOptions) {
        return this.transport.createAutoReplyScenario(data, options);
    }

    updateAutoReplyScenario(scenarioId: string, data: AutoReplyScenarioPayload, options?: EndpointOptions) {
        return this.transport.updateAutoReplyScenario(scenarioId, data, options);
    }

    deleteAutoReplyScenario(scenarioId: string, options?: EndpointOptions) {
        return this.transport.deleteAutoReplyScenario(scenarioId, options);
    }

    reorderAutoReplyScenarios(orderedIds: string[], options?: EndpointOptions) {
        return this.transport.reorderAutoReplyScenarios(orderedIds, options);
    }

    getAnswerDraft(reviewFeedItemId: string, options?: EndpointOptions) {
        return this.transport.getAnswerDraft(reviewFeedItemId, options);
    }

    approveAnswerDraft(reviewFeedItemId: string, data: ApproveReviewAnswerDraftPayload, options?: EndpointOptions) {
        return this.transport.approveAnswerDraft(reviewFeedItemId, data, options);
    }

    async downloadAibackReportPdf(payload: AibackReportPdfPayloadEntity, options?: EndpointOptions) {
        return await this.transport.downloadAibackReportPdf(payload, options);
    }

    async downloadCsv(query: DownloadCsvQuery, options?: EndpointOptions): Promise<DownloadCsvResult> {
        return await this.transport.downloadCsv(query, options);
    }
}

export const createMarketplaceReviewsRepository = () => new MarketplaceReviewsRepository();

export const marketplaceReviewsRepository = createMarketplaceReviewsRepository();

export type {
    DownloadCsvQuery,
    DownloadCsvResult,
    EnqueueAiSelectedAnalysisPayload,
    EnqueueAiSelectedAnalysisResponse,
    GenerateAiAnswerOptions,
    GenerateAiAnswerPayload,
    GenerateAiAnswerResponse,
    GenerateManualAiAnswerPayload,
    GetReviewIdsForSelectionQuery,
    GetReviewsQuery,
    GetReportSummaryOptions,
    ReviewsReportSummaryQuery,
    SelectedAutoRepliesRunPayload,
};
